<template>
  <pilot-dialog
    position="right"
    :scrollable="scrollable"
    :minify="!isPilotPlayerExpanded"
    :no-header="!isPilotPlayerExpanded"
    :title="$t('pilotTours')"
  >
    <template #header-left>
      <button
        v-tooltip.bottom="{
          content: $t('selectPilot')
        }"
        class="pilot-tooltip-btn"
        @click="showPilotList"
      >
        <i class="isif isif-pilot-hamburger" />
      </button>
    </template>
    <template #header-title-icon-right>
      <button
        v-tooltip.bottom="{
          content: $t('scaleDown')
        }"
        class="pilot-tooltip-btn"
        @click="toggleExpand"
      >
        <i
          class="isif"
          :class="{
            'isif-pilot-chevron-up': isLargeScreen,
            'isif-pilot-chevron-down': !isLargeScreen
          }"
        />
      </button>
    </template>
    <template #header-right>
      <button
        v-tooltip.bottom="{
          content: $t('pilotStop')
        }"
        class="pilot-tooltip-btn"
        @click="controlPilotPlayer('stop')"
      >
        <i class="isif isif-close" />
      </button>
    </template>
    <template #body>
      <div
        v-if="isPilotPlayerExpanded"
        class="expanded"
      >
        <div
          v-if="pilotTour"
          class="pilot-tour"
        >
          <div class="pilot-tour__thumbnail">
            <img
              v-if="pilotTour.pilotThumbnail || pilotTour.buildingThumbnail"
              :src="pilotTour.pilotThumbnail || pilotTour.buildingThumbnail"
            >
            <i
              v-else
              class="isif isif-man"
            />
          </div>
          <div
            v-if="isOverTitleLength(pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName)"
            class="pilot-tour__title pilot-tour__title--marquee"
          >
            <div class="marquee-text">
              {{ pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName }}
            </div>
          </div>
          <div
            v-else
            class="pilot-tour__title"
          >
            {{ pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName }}
          </div>
          <div class="pilot-tour__progress">
            <div
              v-for="(item, index) in tour.naviCollection"
              :key="item.objectId"
              class="progress-bar"
              :class="{ 'progress-bar--active': index === currentChapterIndex }"
              :style="{
                width: chapterProgressWidth(item.duration)
              }"
            >
              <div
                class="progress-bar__value"
                :style="{
                  transform: `scaleX(${chapterProgressValue(index, item.duration)})`
                }"
              />
            </div>
          </div>
          <div class="pilot-tour__timeline">
            <div class="current-time">
              {{ displayCumulativeDuration }}
            </div>
            <div class="duration">
              {{ displayTotalDuration }}
            </div>
          </div>
          <div class="pilot-tour__controls">
            <button
              v-tooltip.bottom="{
                content: $t('pilotReplay')
              }"
              class="pilot-tooltip-btn"
              @click="controlPilotPlayer('replay')"
            >
              <i class="isif isif-pilot-replay" />
            </button>
            <div class="group">
              <button
                v-tooltip.bottom="{
                  content: $t('pilotPrevious')
                }"
                class="pilot-tooltip-btn"
                @click="controlPilotPlayer('backward')"
              >
                <i class="isif isif-pilot-backward" />
              </button>
              <button
                v-tooltip.bottom="{
                  content: isPilotPlaying ? $t('pilotPause') : $t('pilotPlay')
                }"
                class="pilot-tooltip-btn"
                @click="togglePilotPlayerStatus"
              >
                <i
                  class="isif"
                  :class="{
                    'isif-pilot-pause': isPilotPlaying,
                    'isif-pilot-play': !isPilotPlaying,
                  }"
                />
              </button>
              <button
                v-tooltip.bottom="{
                  content: $t('pilotNext')
                }"
                class="pilot-tooltip-btn"
                @click="controlPilotPlayer('forward')"
              >
                <i class="isif isif-pilot-forward" />
              </button>
            </div>
            <button
              v-tooltip.bottom="{
                content: isPilotSelfBrowsingMode ? $t('pilotTurnOffSelfBrowsingMode') : $t('pilotTurnOnSelfBrowsingMode')
              }"
              class="pilot-tooltip-btn"
              @click="togglePilotSelfBrowsingMode"
            >
              <i
                class="isif"
                :class="{
                  'isif-close-flag': isPilotSelfBrowsingMode,
                  'isif-flag': !isPilotSelfBrowsingMode,
                }"
                :style="{
                  color: isPilotSelfBrowsingMode ? '#FFFFFF' : '#EC0974'
                }"
              />
            </button>
          </div>
          <div class="pilot-tour__chapters-title">
            {{ $t('pilotChaptersTitle') }}
          </div>
          <div
            v-horizontal-drag-scrolling="{
              startX: chaptersStartX,
              scrollLeft: chaptersScrollLeft,
              isMousedown: isChaptersMousedown
            }"
            class="pilot-tour__chapters"
          >
            <div
              v-for="(item, index) in tour.naviCollection"
              :key="item.objectId"
              class="pilot-tour-chapter-card"
              @click="selectPilotChapter(index)"
            >
              <div
                class="pilot-tour-chapter-card__thumbnail"
                :class="{
                  'pilot-tour-chapter-card__thumbnail--active': index === currentChapterIndex
                }"
              >
                <img
                  v-if="item.thumbnail"
                  :src="item.thumbnail"
                >
                <i
                  v-else
                  class="isif isif-man"
                />
              </div>
              <div class="pilot-tour-chapter-card__title">
                {{ item.name }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="pilotTour.description || showPilotTourImportantContent"
          class="pilot-tour__more-content"
        >
          <button
            class="read-more-btn"
            @click="onClickReadMore"
          >
            <i class="isif isif-pilot-caret-down" />
            <span>{{ $t('pilotReadMoreBtnLabel') }}</span>
          </button>
          <div
            v-if="pilotTour.description"
            ref="introductionRef"
            class="introduction"
          >
            <div class="introduction__title">
              {{ $t('PilotDescription') }}
            </div>
            <div class="introduction__text">
              {{ pilotTour.description }}
            </div>
          </div>
          <div
            v-if="showPilotTourImportantContent"
            ref="importantContentRef"
            class="important-content"
          >
            <div class="important-content__title">
              {{ $t('PilotDetailedInformation') }}
            </div>
            <div
              v-for="item in 3"
              :key="item"
              class="important-content__details"
            >
              <img
                v-if="pilotTour[`pilotDetailedImage${item}`]"
                :src="pilotTour[`pilotDetailedImage${item}`]"
              >
              <div v-if="pilotTour[`pilotDetailedDescription${item}`]">
                {{ pilotTour[`pilotDetailedDescription${item}`] }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="shrink"
      >
        <div
          v-if="pilotTour"
          class="pilot-tour"
        >
          <div
            class="pilot-tour__thumbnail"
            @click="toggleExpand"
          >
            <img
              v-if="pilotTour.pilotThumbnail || pilotTour.buildingThumbnail"
              :src="pilotTour.pilotThumbnail || pilotTour.buildingThumbnail"
            >
            <i
              v-else
              class="isif isif-man"
            />
          </div>
          <div
            class="pilot-tour__content"
            @click="toggleExpand"
          >
            <div
              v-if="isOverTitleLength(pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName)"
              class="pilot-tour-title pilot-tour-title--marquee"
            >
              <div class="marquee-text">
                {{ pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName }}
              </div>
            </div>
            <div
              v-else
              class="pilot-tour-title"
            >
              {{ pilotTour.name + '・' + (currentPanorama.customCategory || $t(currentPanorama.category)) + '・' + pilotTour.ownerName }}
            </div>
            <div class="pilot-tour-progress">
              <div
                v-for="(item, index) in tour.naviCollection"
                :key="item.objectId"
                class="progress-bar"
                :class="{ 'progress-bar--active': index === currentChapterIndex }"
                :style="{
                  width: chapterProgressWidth(item.duration)
                }"
              >
                <div
                  class="progress-bar__value"
                  :style="{
                    transform: `scaleX(${chapterProgressValue(index, item.duration)})`
                  }"
                />
              </div>
            </div>
          </div>
          <div class="pilot-tour__controls">
            <button
              v-tooltip.bottom="{
                content: isPilotPlaying ? $t('pilotPause') : $t('pilotPlay')
              }"
              class="pilot-tooltip-btn"
              @click="togglePilotPlayerStatus"
            >
              <i
                class="isif"
                :class="{
                  'isif-pilot-pause': isPilotPlaying,
                  'isif-pilot-play': !isPilotPlaying,
                }"
              />
            </button>
            <button
              v-tooltip.bottom="{
                content: isPilotSelfBrowsingMode ? $t('pilotTurnOffSelfBrowsingMode') : $t('pilotTurnOnSelfBrowsingMode')
              }"
              class="pilot-tooltip-btn"
              @click="togglePilotSelfBrowsingMode"
            >
              <i
                class="isif"
                :class="{
                  'isif-close-flag': isPilotSelfBrowsingMode,
                  'isif-flag': !isPilotSelfBrowsingMode,
                }"
                :style="{
                  color: isPilotSelfBrowsingMode ? '#FFFFFF' : '#EC0974'
                }"
              />
            </button>
            <button
              v-tooltip.bottom="{
                content: $t('pilotStop')
              }"
              class="pilot-tooltip-btn"
              @click="controlPilotPlayer('stop')"
            >
              <i class="isif isif-close" />
            </button>
          </div>
        </div>
      </div>
      <audio
        ref="audioRef"
        preload="none"
        @timeupdate="updateChapterCurrentTime"
      />
    </template>
  </pilot-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import {
  fetchPilotChapter as fetchPilotChaptersFromFirebase,
  updatePilotViewCounts
} from '@/plugins/firebase'
import gaEvents from '@/api/ga-events'
import PilotDialog from './PilotDialog'

export default {
  name: 'PilotTour',
  components: {
    PilotDialog
  },
  props: {
    pilotTour: {
      type: Object,
      default () {
        return {}
      }
    },
    isLargeScreen: {
      type: Boolean
    }
  },
  data () {
    return {
      timer: null,
      chapterCurrentTime: 0,
      chapterDuration: 0,
      tour: {
        naviCollection: []
      },
      currentChapterIndex: 0,
      currentHistoryIndex: 0,
      watchedPilots: [],
      currentTimeIntervalId: null,
      historyTimeoutId: null,
      audioTimeoutId: null,
      isAudioStopTooEarly: false,
      chaptersStartX: 0,
      chaptersScrollLeft: 0,
      isChaptersMousedown: false
    }
  },
  computed: {
    ...mapGetters([
      'panoramas',
      'currentBuilding',
      'currentPanorama',
      'isPilotListActive',
      'isPilotPlayerActive',
      'isPilotPlaying',
      'isPilotPaused',
      'isFloorplanActive',
      'isLocationActive',
      'isMarkerInfoActive',
      'isPopupActive',
      'isPilotPlayerExpanded',
      'isPanoramasListActive',
      'isShareActive',
      'markerInfoData',
      'pauseTimestamp',
      'krpanoEl',
      'krpanoHooks',
      'krpanoLookAtH',
      'krpanoXOffset',
      'isPilotSelfBrowsingMode'
    ]),
    audioRef () {
      return this.$refs.audioRef
    },
    cumulativeDuration () {
      if (this.tour.naviCollection.length > 0 && this.tour.naviCollection[this.currentChapterIndex]) {
        return this.tour.naviCollection[this.currentChapterIndex].cumulativeDuration + this.chapterCurrentTime
      } else {
        return 0
      }
    },
    totalDuration () {
      return this.tour.naviCollection.map(chapter => chapter.duration).reduce((acc, cur) => acc + cur, 0)
    },
    displayCumulativeDuration () {
      return this.formatTime(this.cumulativeDuration)
    },
    displayTotalDuration () {
      return this.formatTime(this.totalDuration)
    },
    showPilotTourImportantContent () {
      return !_.isNil(this.pilotTour.pilotDetailedImage1) ||
             !_.isNil(this.pilotTour.pilotDetailedDescription1) ||
             !_.isNil(this.pilotTour.pilotDetailedImage2) ||
             !_.isNil(this.pilotTour.pilotDetailedDescription2) ||
             !_.isNil(this.pilotTour.pilotDetailedImage3) ||
             !_.isNil(this.pilotTour.pilotDetailedDescription3)
    },
    scrollable () {
      return Boolean(this.isPilotPlayerExpanded && (this.pilotTour.description || this.showPilotTourImportantContent))
    }
  },
  watch: {
    pauseTimestamp: {
      async  handler () {
        if (this.isPilotPlaying) {
          await this.pause(false, false)
        }
      }
    },
    isPilotSelfBrowsingMode: {
      handler (value) {
        if (value) {
          const h = this.$createElement
          this.$message({
            type: 'info',
            customClass: 'pilot-tour-message pilot-tour-message--info',
            message: h('div', { style: 'padding-right: 16px;' }, [
              h('p', { style: 'font-weight: 500;font-size: 16px;line-height: 24px;letter-spacing: 0.25px;color: #FFFFFF;' }, this.$t('pilotSelfBrowsingModeMessageTitle')),
              h('p', { style: 'line-height: 20px;letter-spacing: 0.25px;color: #FFFFFF;' }, this.$t('pilotSelfBrowsingModeMessageContent'))
            ]),
            duration: 5000,
            offset: 100,
            showClose: true
          })
        } else {
          this.selectPilotChapter(this.currentChapterIndex)
        }
      }
    }
  },
  mounted () {
    // RM# 6568, update chapterCurrentTime after the audio has stopped
    if (!this.currentTimeIntervalId) {
      this.currentTimeIntervalId = setInterval(() => {
        if (this.isAudioStopTooEarly && this.isPilotPlaying) {
          this.chapterCurrentTime += 0.3
        }
      }, 300)
    }
  },
  beforeDestroy () {
    clearInterval(this.currentTimeIntervalId)
  },
  methods: {
    ...mapActions([
      'startPilotPlayer',
      'stopPilotPlayer',
      'pausePilotPlayer',
      'togglePilotPlayerExpansion',
      'toggleFloorplan',
      'toggleLocation',
      'toggleMarkerInfo',
      'showPopup',
      'closePopup',
      'togglePanoramasList',
      'toggleShare',
      'setMarkerInfoData',
      'setPopupMarker',
      'setPopupUrl',
      'selectBuilding',
      'selectPanorama',
      'setCurrentPilotData',
      'stopAutoRotate',
      'pauseAudio',
      'pauseMusic',
      'pauseAllVideo',
      'closePilotList',
      'showPilotList',
      'togglePilotSelfBrowsingMode'
    ]),
    toggleExpand () {
      this.closePilotList()
      this.togglePilotPlayerExpansion()
    },
    isOverTitleLength (title) {
      if (this.isPilotPlayerExpanded) {
        // 300px is max width, title font-size is 18px
        const maxLength = Math.floor(300 / 18)
        return title.length > maxLength
      } else {
        // 196px is max width, title font-size is 12px
        const maxLength = Math.floor(196 / 12)
        return title.length > maxLength
      }
    },
    chapterProgressWidth (duration) {
      if (this.isPilotPlayerExpanded) {
        // 300px is max width
        return `${300 * (duration / this.totalDuration)}px`
      } else {
        // 197px is max width
        return `${197 * (duration / this.totalDuration)}px`
      }
    },
    chapterProgressValue (index, duration) {
      if (index > this.currentChapterIndex) {
        return 0
      } else if (index < this.currentChapterIndex) {
        return 1
      } else if (!duration || !this.chapterCurrentTime || this.chapterCurrentTime < 0) {
        return 0
      } else {
        return this.chapterCurrentTime / duration
      }
    },
    updateChapterCurrentTime () {
      const startTime = this.tour.naviCollection.length > 0 && this.tour.naviCollection[this.currentChapterIndex] ? this.tour.naviCollection[this.currentChapterIndex].startTime : 0
      this.chapterCurrentTime = this.audioRef.currentTime - startTime
    },
    formatTime (value) {
      if (!value) {
        return '00:00'
      }

      let hours = Math.floor(value / 3600)
      let minutes = Math.floor(value % 3600 / 60)
      let seconds = Math.floor(value % 60)

      if (hours < 10) {
        hours = '0' + hours
      }

      if (minutes < 10) {
        minutes = '0' + minutes
      }

      if (seconds < 10) {
        seconds = '0' + seconds
      }
      return `${hours}:${minutes}:${seconds}`
    },
    async initialize () {
      try {
        const chapters = await fetchPilotChaptersFromFirebase(this.pilotTour.objectId)
        this.currentChapterIndex = chapters.findIndex(chapter => chapter.panoramaId === this.currentPanorama.objectId)
        if (this.currentChapterIndex === -1) {
          // not found
          this.$emit('close', 'pilot-tour')
          const query = Object.assign({}, this.$route.query)
          delete query.pilot
          return this.$router.replace({
            query
          })
        }
        this.audioRef.src = chapters[this.currentChapterIndex].videoUrl
        this.chapterDuration = chapters[this.currentChapterIndex].duration

        this.tour.naviCollection = chapters.map(chapter => {
          const panorama = this.panoramas.find(panorama => panorama.objectId === chapter.panoramaId)
          return {
            ...chapter,
            name: panorama ? (panorama.customCategory || this.$t(panorama.category)) : '',
            thumbnail: panorama ? panorama.thumbnail : ''
          }
        })
        this.setCurrentPilotData({
          ...this.pilotTour,
          ...this.tour
        })
        this.togglePilotPlayerStatus()
        this.$router.replace({
          query: {
            ...this.$route.query,
            pilot: this.pilotTour.objectId
          }
        })
      } catch (error) {
        throw Error(`fetchTourChapters Error: ${error}`)
      }
    },
    async togglePilotPlayerStatus () {
      if (this.isPilotPlaying) { // pause
        await this.pause()
      } else if (this.isPilotPaused) { // resume
        this.stopAutoRotate()
        this.pauseAudio()
        this.pauseMusic()
        this.pauseAllVideo()
        this.resume()
      } else { // start
        // for mobile, need to "unlock" the file on user interaction
        await this.audioRef.play()
        await this.audioRef.pause()
        this.stopAutoRotate()
        this.pauseAudio()
        this.pauseMusic()
        this.pauseAllVideo()
        this.start(this.currentChapterIndex)
        if (!this.watchedPilots.includes(this.pilotTour.objectId)) {
          this.watchedPilots.push(this.pilotTour.objectId)
          gaEvents.sendEvent('Pilot', 'PlayPilot', this.pilotTour.objectId)
          updatePilotViewCounts(this.pilotTour.objectId)
        }
      }
    },
    async controlPilotPlayer (action) {
      switch (action) {
        case 'replay':
          this.selectPilotChapter(0)
          break
        case 'backward':
          if (this.chapterCurrentTime > 3) {
            this.selectPilotChapter(this.currentChapterIndex)
          } else if (this.currentChapterIndex > 0) {
            this.selectPilotChapter(this.currentChapterIndex - 1)
          }
          break
        case 'forward':
          if (this.currentChapterIndex < this.tour.naviCollection.length - 1) {
            this.selectPilotChapter(this.currentChapterIndex + 1)
          }
          break
        case 'stop':
          await this.pause(true)
          this.$emit('close', 'pilot-tour')
          this.setCurrentPilotData({})
          break
        default:
          break
      }
    },
    async start (chapterIndex = 0, historyIndex = 0, resume = false) {
      if (chapterIndex >= this.tour.naviCollection.length || historyIndex >= this.tour.naviCollection[chapterIndex].naviData.history.length) {
        await this.pause(true)
        /**
         * since the duration is not accurate, in order to make the progress bar look good
         * set current time to the duration when video ended
         */
        this.audioRef.addEventListener('pause', () => {
          this.isAudioStopTooEarly = false
          this.chapterCurrentTime = this.chapterDuration
        }, {
          once: true
        })
        this.chapterCurrentTime = this.chapterDuration
      } else {
        this.startPilotPlayer()
        this.currentChapterIndex = chapterIndex
        if (!resume) {
          this.audioRef.removeEventListener('pause', () => {
            if (this.isPilotPlaying) {
              this.isAudioStopTooEarly = true
            }
          })
          this.isAudioStopTooEarly = false
          this.chapterCurrentTime = 0
        }

        const chapter = this.tour.naviCollection[chapterIndex]
        const { cameraH } = chapter.naviData.history[historyIndex]
        // to the first building, panorama, angle
        if (chapter.buildingId !== this.currentBuilding.objectId) {
          await this.audioRef.pause()
          this.selectBuilding({
            buildingId: chapter.buildingId,
            panorama: chapter.panoramaId
          })
          this.krpanoHooks.one('startup')
        } else if (chapter.panoramaId !== this.currentPanorama.objectId) {
          if (!this.isPilotSelfBrowsingMode) {
            if (chapter.naviData.animation) {
              window.krpanoObj.call(`prepare_change_scene(panorama_${chapter.panoramaId}, ${chapter.panoramaId}, 'Hotspot', ${cameraH}, ${true}, ${this.krpanoLookAtH});`)
              this.krpanoHooks.one('change_scene')
            } else {
              this.selectPanorama({
                objectId: chapter.panoramaId
              })
              this.krpanoHooks.one('change_scene')
            }
          }
        }
        this.syncHistoryView(chapter.naviData.history[historyIndex])
        let resumeHistoryDelay = 0
        if (resume && !this.isAudioStopTooEarly) {
          resumeHistoryDelay = historyIndex * 0.5 - (this.audioRef.currentTime - chapter.startTime)
          resumeHistoryDelay = resumeHistoryDelay >= 0 ? resumeHistoryDelay * 1000 : 0
        }
        this.historyTimeoutId = setTimeout(() => {
          this.playHistory(historyIndex + 1)
        }, 500 + resumeHistoryDelay)
        if (this.audioRef.src !== chapter.videoUrl) {
          this.audioRef.src = chapter.videoUrl
        }
        if (!resume && Math.abs(chapter.startTime - this.audioRef.currentTime) > 1.5) {
          if (this.audioRef.readyState === 4) {
            this.audioRef.currentTime = chapter.startTime
          } else {
            this.audioRef.addEventListener('canplay', () => {
              this.audioRef.currentTime = chapter.startTime
            }, {
              once: true
            })
          }
        }
        this.audioTimeoutId = setTimeout(async () => {
          if (!(resume && this.isAudioStopTooEarly)) {
            await this.audioRef.play()
          }
          this.audioRef.addEventListener('pause', () => {
            if (this.isPilotPlaying) {
              this.isAudioStopTooEarly = true
            }
          }, {
            once: true
          })
        }, 500)
      }
    },
    async pause (stop = false, stopCamera = true) {
      if (stop) {
        this.stopPilotPlayer()
      } else {
        this.pausePilotPlayer()
      }
      clearTimeout(this.historyTimeoutId)
      clearTimeout(this.audioTimeoutId)
      await this.audioRef.pause()
      if (stopCamera) {
        await window.krpanoObj.call('stop_camera();')
      }
    },
    resume () {
      this.start(this.currentChapterIndex, this.currentHistoryIndex, true)
    },
    async playPilot () {
      await this.pause(true)
      this.chapterCurrentTime = 0
      this.chapterDuration = 0
      this.initialize()
    },
    async selectPilotChapter (index) {
      await this.pause(true)
      const chapter = this.tour.naviCollection[index]
      if (chapter.buildingId !== this.currentBuilding.objectId) {
        await this.audioRef.pause()
        const building = {
          buildingId: chapter.buildingId,
          panorama: chapter.panoramaId
        }
        this.selectBuilding(building)
        this.krpanoHooks.one('startup')
      } else if (chapter.panoramaId !== this.currentPanorama.objectId) {
        this.selectPanorama({
          objectId: chapter.panoramaId
        })
        this.krpanoHooks.one('change_scene')
      }
      this.currentChapterIndex = index
      this.audioRef.currentTime = chapter.startTime
      this.chapterCurrentTime = 0
      this.chapterDuration = chapter.duration
      this.$nextTick(() => {
        this.togglePilotPlayerStatus()
      })
    },
    playHistory (index = 0) {
      const chapter = this.tour.naviCollection[this.currentChapterIndex]
      this.chapterDuration = chapter.duration
      const history = chapter.naviData.history
      if (index < history.length) {
        this.currentHistoryIndex = index
        this.syncHistoryView(history[index])
        // adjust speed to sync with the video
        const maximumSpeedUp = 1.3
        const syncTimeOffsetLimitMax = (500 * maximumSpeedUp) - 500
        const syncTimeOffsetLimitMin = 500 - (500 / maximumSpeedUp)
        let syncTimeOffset = 0
        if (!this.isAudioStopTooEarly) {
          syncTimeOffset = ((index - 1) * 0.5 - (this.audioRef.currentTime - chapter.startTime)) * 1000
          syncTimeOffset = Math.min(syncTimeOffset, syncTimeOffsetLimitMax)
          syncTimeOffset = Math.max(syncTimeOffset, -syncTimeOffsetLimitMin)
        }
        this.historyTimeoutId = setTimeout(() => this.playHistory(index + 1), 500 + syncTimeOffset)
      } else {
        this.start(this.currentChapterIndex + 1)
      }
    },
    syncHistoryView (historyData) {
      if (!this.isPilotSelfBrowsingMode) {
        const { floorplan, location, share, panoramaList, markerInfo, markerId, popup, popupId, cameraH, cameraV, cameraFov } = historyData
        if (!_.isNil(floorplan) && floorplan !== this.isFloorplanActive) {
          this.toggleFloorplan()
        }
        if (!_.isNil(location) && location !== this.isLocationActive) {
          this.toggleLocation()
        }
        if (!_.isNil(share) && share !== this.isShareActive) {
          this.toggleShare()
        }
        if (!_.isNil(panoramaList) && panoramaList !== this.isPanoramasListActive) {
          this.togglePanoramasList()
        }
        if (!_.isNil(markerInfo) && markerInfo !== this.isMarkerInfoActive) {
          this.toggleMarkerInfo()
          const markerInfoData = this.currentPanorama.markers.find(marker => marker.objectId === markerId) || {}
          this.setMarkerInfoData(markerInfoData)
        }
        if (!_.isNil(popup)) {
          popup === true ? this.showPopup() : this.closePopup()
          const popupData = this.currentPanorama.markers.find(marker => marker.objectId === popupId) || {}
          this.setPopupUrl(popupData.actionLink)
          this.setPopupMarker(popupData)
        }
        window.krpanoObj.call(`set_camera(${_.isNil(cameraH) ? null : cameraH}, ${_.isNil(cameraV) ? null : cameraV}, ${_.isNil(cameraFov) ? null : cameraFov});`)
      }
    },
    onClickReadMore () {
      if (this.$refs.introductionRef) {
        this.$refs.introductionRef.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      } else if (this.$refs.importantContentRef) {
        this.$refs.importantContentRef.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@import './style'

.expanded {

  .pilot-tour {
    &__thumbnail {
      text-align: center

      img {
        width: 160px
        height: 160px
        object-fit: cover
      }

      i {
        background-color: $white
        font-size: 160px
      }
    }

    &__title {
      color: $white
      font-size: 18px
      font-weight: 400
      white-space: nowrap
      width: 100%
      max-width: 312px
      margin: 16px auto 0 auto
      text-align: center

      &--marquee {
        overflow: hidden

        .marquee-text {
          animation: 15s marquee-text linear infinite
        }
      }
    }

    &__progress {
      width: 100%
      max-width: 300px
      margin: 16px auto 0 auto
      text-align: center
      overflow: hidden
      height: 13px
      padding: 5px 0
      display: flex
      align-items: center
      gap: 2px

      .progress-bar {
        width: 0
        height: 2px
        background-color: $white

        &--active {
          height: 5px
        }

        &__value {
          background-color: #cc0100
          width: 100%
          height: 100%
          transform-origin: left center
          transition: transform .06s linear
        }
      }
    }

    &__timeline {
      width: 100%
      max-width: 300px
      display: flex
      margin: 0 auto
      justify-content: space-between

      .current-time,
      .duration {
        font-size: 12px
        color: $white
      }
    }

    &__controls {
      width: 100%
      max-width: 312px
      display: flex
      justify-content: space-between
      align-items: center
      margin: 16px auto 0 auto
      .group {
        display: flex
        align-items: center
        gap: 20px
      }
    }

    &__chapters-title {
      margin: 16px auto
      color: $white
      font-size: 14px
      font-weight: 400
      white-space: nowrap
      width: 100%
      max-width: 312px
    }

    &__chapters {
      width: 100%
      display: flex
      align-items: center
      overflow-x: auto
      overflow-y: hidden
      height: 140px
      scrollbars(5px, transparent, transparent)
      gap: 32px

      .pilot-tour-chapter-card {
        cursor: pointer

        &__thumbnail {
          text-align: center

          &--active {

            img {
              width: 70px
              height: 70px
              border-radius: 50%
              object-fit: cover
              border: 2px solid #df348f
            }

            i {
              background-color: $white
              border-radius: 50%
              font-size: 70px
              border: 2px solid #df348f
            }
          }

          img {
            width: 70px
            height: 70px
            border-radius: 50%
            object-fit: cover
          }

          i {
            background-color: $white
            border-radius: 50%
            font-size: 70px
          }
        }

        &__title {
          text-align: center
          font-size: 12px
          color: $white
          overflow: hidden
          text-overflow: ellipsis
          white-space: nowrap
          width: 70px
        }
      }
    }

    &__more-content {
      width: 100%

      .read-more-btn {
        display: flex
        align-items: center
        padding: 4px 6px
        background-color: $white
        border-radius: 16px
        margin: 16px auto

        i {
          color: #333333
          font-size: 24px
        }

        span {
          color: #333333
          font-size: 14px
          text-shadow: initial
        }
      }

      .introduction {

        &__title {
          font-size: 14px
          font-weight: 400
          color: $white
        }

        &__text {
          margin-top: 16px
          font-size: 12px
          font-weight: 400
          color: $white
        }
      }

      .important-content {

        &__title {
          font-size: 14px
          font-weight: 400
          color: $white
        }

        &__details {
          margin-top: 24px

          img {
            width: 100%
          }

          div {
            margin-top: 16px
            font-size: 12px
            font-weight: 400
            color: $white
          }
        }
      }

      .introduction + .important-content {
        margin-top: 16px
      }
    }

    @media (min-width: 1024px) {
      &__chapters,
      &__more-content {
        padding: 0 20px
      }
    }
  }
}

.shrink {

  .pilot-tour {
    display: flex
    align-items: center
    justify-content: space-between

    &__thumbnail {
      flex: 1
      cursor: pointer

      img {
        width: 50px
        height: 50px
        border-radius: 4px
        object-fit: cover
      }

      i {
        font-size: 50px
        border-radius: 4px
        background-color: $white
      }
    }

    &__content {
      flex: 1
      text-align: center
      cursor: pointer
      max-width: 184px
      @media (min-width: 1024px) {
        min-width: 196px
      }

      .pilot-tour-title {
        color: $white
        font-size: 12px
        font-weight: 400
        white-space: nowrap
        width: 100%

        &--marquee {
          overflow: hidden

          .marquee-text {
            animation: 15s marquee-text linear infinite
          }
        }
      }

      .pilot-tour-progress {
        width: 100%
        text-align: center
        overflow: hidden
        height: 13px
        padding: 5px 0
        margin-top: 8px
        display: flex
        align-items: center
        gap: 2px

        .progress-bar {
          width: 0
          height: 2px
          background-color: $white

          &--active {
            height: 5px
          }

          &__value {
            background-color: #cc0100
            width: 100%
            height: 100%
            transform-origin: left center
            transition: transform .06s linear
          }
        }
      }
    }

    &__controls {
      flex: 1
      display: flex
      justify-content: flex-end
      min-width: 108px
      @media (min-width: 1024px) {
        min-width: 132px
      }
    }
  }
}

.isif-pilot-chevron-down,
.isif-pilot-chevron-up,
.isif-pilot-caret-down {
  transform: scale(.6)
}

 @keyframes marquee-text {
  0% {
    transform: translate(0%)
  }

  100% {
    transform: translate(-100%)
  }
}
</style>
<style lang="stylus">
// Custom element-ui message css
.pilot-tour-message {
  border: none
  border-radius: 8px
  align-items: flex-start
  @media (max-width: 1023px) {
    min-width: 327px
    top: auto !important
    bottom: 100px !important
  }
  &--info {
    background-color: #376FD5 !important
  }
  .el-message__icon.el-icon-info,
  .el-message__closeBtn.el-icon-close {
    color: #FFFFFF
    font-size: 24px
  }
}
</style>
